import { getSupabaseConfig } from '../supabase/config'
import { readCachedIdentity } from '../auth/cachedIdentity'
import { workspaceDatabaseName } from '../data/workspace'
import { webShareWithOriginal } from '../utils/urlIntake'

const SHARE_DATABASE_NAME = 'minimany-share-target-v1'
const SHARE_STORE_NAME = 'incoming-shares'

export interface SharedImageFile {
  blob: Blob
  name: string
  type: string
  lastModified: number
}

export interface IncomingShare {
  id: string
  body: string
  /** The share before its packaging was trimmed, when trimming changed it. */
  original?: string
  files: SharedImageFile[]
  createdAt: number
  workspaceBinding?: string
}

function openShareDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SHARE_DATABASE_NAME, 1)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(SHARE_STORE_NAME)) {
        request.result.createObjectStore(SHARE_STORE_NAME, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}

function sharedBodyWithOriginal(formData: FormData): { body: string; original: string | null } {
  const value = (key: string) => {
    const field = formData.get(key)
    return typeof field === 'string' ? field : ''
  }
  return webShareWithOriginal(value('title'), value('text'), value('url'))
}

function createShareId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `share-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function incomingShareFromFormData(
  formData: FormData,
  id = createShareId(),
  now = Date.now(),
): IncomingShare {
  const files = formData.getAll('images').flatMap((value) => {
    if (typeof value === 'string') return []
    return [{
      blob: value,
      name: value.name,
      type: value.type,
      lastModified: value.lastModified,
    }]
  })
  const { body, original } = sharedBodyWithOriginal(formData)
  return { id, body, ...(original ? { original } : {}), files, createdAt: now }
}

export async function storeIncomingShare(share: IncomingShare): Promise<void> {
  // The service worker records the receiving identity before redirecting. A later
  // account must not consume this private payload from the shared inbox.
  const config = getSupabaseConfig()
  const identity = config ? await readCachedIdentity(config) : null
  const workspaceBinding = workspaceDatabaseName({ project: config?.url ?? 'local', environment: config?.environment ?? 'local' }, identity?.user.id ?? null)
  const database = await openShareDatabase()
  try {
    const transaction = database.transaction(SHARE_STORE_NAME, 'readwrite')
    transaction.objectStore(SHARE_STORE_NAME).put({ ...share, workspaceBinding })
    await transactionDone(transaction)
  } finally {
    database.close()
  }
}

export async function takeIncomingShare(id: string, expectedWorkspace: string, acknowledge = true): Promise<IncomingShare | null> {
  const database = await openShareDatabase()
  try {
    const transaction = database.transaction(SHARE_STORE_NAME, 'readwrite')
    const store = transaction.objectStore(SHARE_STORE_NAME)
    const request = store.get(id)
    const share = await new Promise<IncomingShare | undefined>((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result as IncomingShare | undefined
        if (result && result.workspaceBinding !== expectedWorkspace) { resolve(undefined); return }
        if (result && acknowledge) store.delete(id)
        resolve(result)
      }
      request.onerror = () => reject(request.error)
    })
    await transactionDone(transaction)
    return share ?? null
  } finally {
    database.close()
  }
}

export async function countIncomingWorkspaceShares(workspaceBinding: string): Promise<number> {
  const database = await openShareDatabase()
  try {
    const tx = database.transaction(SHARE_STORE_NAME, 'readonly')
    const request = tx.objectStore(SHARE_STORE_NAME).getAll()
    return await new Promise<number>((resolve, reject) => {
      request.onsuccess = () => resolve((request.result as IncomingShare[]).filter(share => share.workspaceBinding === workspaceBinding).length)
      request.onerror = () => reject(request.error)
    })
  } finally { database.close() }
}
